/**
 * CookieConsent — GDPR-compliant cookie consent banner
 *
 * Displays on first visit (or when consent state is missing).
 * Three options: Accept All, Reject Optional, Customise.
 * Consent state is persisted via cookies.js utilities.
 *
 * API_TODO: When analytics/marketing cookies are accepted,
 * initialise your analytics SDK here (e.g. Plausible, PostHog).
 */
import { useState, useEffect } from 'react';
import { getConsentState, acceptAllCookies, rejectOptionalCookies, saveConsent } from '../utils/cookies';

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showCustomise, setShowCustomise] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const consent = getConsentState();
    if (!consent) {
      // Small delay so page loads first
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  if (!visible) return null;

  const handleAcceptAll = () => {
    acceptAllCookies();
    setVisible(false);
    // API_TODO: initialise analytics + marketing tracking here
  };

  const handleRejectOptional = () => {
    rejectOptionalCookies();
    setVisible(false);
  };

  const handleSaveCustom = () => {
    saveConsent({ essential: true, analytics, marketing });
    setVisible(false);
    // API_TODO: conditionally initialise tracking based on consent
  };

  return (
    <div className="cookie-banner" role="dialog" aria-label="Cookie consent">
      <div className="cookie-banner-inner">
        {!showCustomise ? (
          <>
            <div className="cookie-banner-text">
              <strong>We value your privacy</strong>
              <p>
                We use essential cookies to make Connekted.in work. With your consent,
                we may also use analytics cookies to improve the platform. We never sell
                your data or serve third-party ads.{' '}
                <a href="/cookies" style={{ color: 'var(--brand)', textDecoration: 'underline' }}>
                  Learn more
                </a>
              </p>
            </div>
            <div className="cookie-banner-actions">
              <button className="btn btn-primary-app btn-sm" onClick={handleAcceptAll}>
                Accept all
              </button>
              <button className="btn btn-outline btn-sm" onClick={handleRejectOptional}>
                Essential only
              </button>
              <button
                className="btn btn-ghost-app btn-sm"
                onClick={() => setShowCustomise(true)}
                style={{ fontSize: 13 }}
              >
                Customise
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="cookie-banner-text">
              <strong>Customise cookies</strong>
              <p style={{ marginBottom: 12 }}>Choose which optional cookies you allow.</p>

              <label className="cookie-option">
                <span>
                  <strong>Essential</strong> — required for the site to function
                </span>
                <input type="checkbox" checked disabled />
              </label>

              <label className="cookie-option">
                <span>
                  <strong>Analytics</strong> — helps us understand usage patterns
                </span>
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                />
              </label>

              <label className="cookie-option">
                <span>
                  <strong>Marketing</strong> — measures our own campaign effectiveness
                </span>
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                />
              </label>
            </div>
            <div className="cookie-banner-actions">
              <button className="btn btn-primary-app btn-sm" onClick={handleSaveCustom}>
                Save preferences
              </button>
              <button
                className="btn btn-ghost-app btn-sm"
                onClick={() => setShowCustomise(false)}
                style={{ fontSize: 13 }}
              >
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
